/**
 * "Add a file" on a visit (1.5.5): photos, PDFs and videos staff add after the fact — "pictures that were not
 * taken that day", "a video the crew brought in". Each goes into the visit's own folder (the one the crew's photos
 * and the report live in), named `<yyyy-mm-dd> <Last, First> <unit> added <n>.<ext>`. Nothing is ever deleted; a
 * wrong file gets the note "added by mistake" and stays.
 */
import type { Role } from './roles';

/** The visit summaries that carry "Add a file". */
export const VISIT_FILE_KINDS = ['housekeeping', 'incident', 'tech', 'outing', 'companion-incident'] as const;
export type VisitFileKind = (typeof VISIT_FILE_KINDS)[number];

/** Who adds files: schedulers, co-administrators and the administrator. The crew and the tech keep their own
 * Finish / Close-out upload; viewers and volunteers nothing. */
export const FILE_ADDERS: readonly Role[] = ['admin', 'coadmin', 'scheduler'];
/** Who opens a file inside the sensitive boundary (photos, reports): the same as the close-out photos. */
export const FILE_OPENERS: readonly Role[] = ['admin', 'coadmin'];

export const MAX_ADD_BYTES = 300 * 1024 * 1024;
export const MAX_VIDEO_SECONDS = 3 * 60;
/** 12 × 320 KiB: Graph upload sessions take chunks in multiples of 320 KiB; under 4 MB keeps each request small. */
export const UPLOAD_CHUNK = 12 * 320 * 1024;

export type Media = 'photo' | 'pdf' | 'video' | 'other';

/** What may be added, by extension (the browser's type is not trusted; a camera-roll .mov is often blank). */
export const ADD_TYPES: Record<string, { type: string; media: Exclude<Media, 'other'> }> = {
  jpg: { type: 'image/jpeg', media: 'photo' },
  jpeg: { type: 'image/jpeg', media: 'photo' },
  png: { type: 'image/png', media: 'photo' },
  webp: { type: 'image/webp', media: 'photo' },
  heic: { type: 'image/heic', media: 'photo' },
  pdf: { type: 'application/pdf', media: 'pdf' },
  mp4: { type: 'video/mp4', media: 'video' },
  mov: { type: 'video/quicktime', media: 'video' },
};

/** The `accept` attribute of the file picker. */
export const ADD_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,.heic,application/pdf,video/mp4,video/quicktime,.mov,.mp4';

export function extOf(name: string): string {
  const m = /\.([A-Za-z0-9]{1,8})$/.exec(name.trim());
  return m ? m[1]!.toLowerCase() : '';
}

/** Photo, PDF, video or other — by extension (also for the crew's own photos and the report in the folder). */
export function mediaOf(name: string): Media {
  return ADD_TYPES[extOf(name)]?.media ?? 'other';
}

export function contentTypeOf(name: string): string {
  return ADD_TYPES[extOf(name)]?.type ?? 'application/octet-stream';
}

export type AddFileProblem = 'type' | 'empty' | 'too-large' | 'too-long' | 'no-length';

/** The rules every added file meets (the page checks first; the API checks again). */
export function checkAddFile(f: { name: string; size: number; durationSeconds?: number | null }): AddFileProblem | null {
  const t = ADD_TYPES[extOf(f.name)];
  if (!t) return 'type';
  if (!(f.size > 0)) return 'empty';
  if (f.size > MAX_ADD_BYTES) return 'too-large';
  if (t.media === 'video') {
    if (f.durationSeconds === null || f.durationSeconds === undefined || !Number.isFinite(f.durationSeconds)) return 'no-length';
    if (f.durationSeconds > MAX_VIDEO_SECONDS + 0.5) return 'too-long';
  }
  return null;
}

/** OneDrive refuses these in a name (the same cleaning as api/src/files/folders.ts safeSegment). */
function clean(s: string): string {
  return (
    s
      .replace(/["*:<>?/\\|#%]/g, ' ')
      // eslint-disable-next-line no-control-regex -- control characters are refused in OneDrive names
      .replace(/[\u0000-\u001f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/[. ]+$/, '')
  );
}

export interface AddedNameParts {
  /** ServiceDateKey (yyyyMMdd) or yyyy-mm-dd — the visit's civil day. */
  day: string;
  /** "Last, First" — or, for a group outing (which never carries a tenant list), the outing's name. */
  who: string;
  unit: string;
}

/** `<yyyy-mm-dd> <Last, First> <unit> added <n>.<ext>` */
export function addedFileName(p: AddedNameParts, n: number, ext: string): string {
  const d = p.day.trim();
  const iso = /^\d{8}$/.test(d) ? `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` : d.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) throw new Error(`visit-files: not a civil day: ${p.day}`);
  const stem = [iso, clean(p.who), clean(p.unit), `added ${n}`].filter(Boolean).join(' ');
  return `${stem}.${ext.replace(/^\./, '').toLowerCase()}`;
}

/** "Last, First" (a missing first name leaves the last name alone). */
export function lastFirst(first: string, last: string): string {
  const f = first.trim();
  const l = last.trim();
  return l && f ? `${l}, ${f}` : l || f;
}

/** The next n: one more than the highest `added <n>` already in the folder or reserved by an upload under way. */
export function nextAddedNumber(names: readonly string[]): number {
  let max = 0;
  for (const n of names) {
    const m = / added (\d+)\.[A-Za-z0-9]+$/i.exec(n);
    if (m) max = Math.max(max, Number(m[1]));
  }
  return max + 1;
}

/* ---------------------------------------------------------------------------------------------- */

export interface VisitFileView {
  name: string;
  size: number;
  media: Media;
  lastModified: string;
  /** Added through "Add a file": who and when (from the visit's log). */
  addedBy: string | null;
  addedAt: string | null;
  /** "added by mistake": the file stays; the note says so. */
  mistake: { by: string; at: string } | null;
  /** The file's own page in SharePoint (graph), when the site gives it. */
  webUrl: string | null;
  /** 1.6.7: the folder it is in — Before/, After/, or the visit's folder itself (null). */
  when: 'before' | 'after' | null;
}

export interface VisitFilesResponse {
  kind: VisitFileKind;
  id: string;
  /** What the summary is about, e.g. "2026-10-02 · Doe, Jane · 5C". */
  title: string;
  folder: string;
  webUrl: string | null;
  /** Newest last. */
  files: VisitFileView[];
  canOpen: boolean;
  /** 1.6.7: a housekeeping visit — adding a file asks "Before or After?" (or Other). */
  asksWhere: boolean;
}

export interface UploadStartBody {
  fileName: string;
  size: number;
  durationSeconds?: number | null;
  /** 1.6.7: Before, After, or Other (the visit's folder itself; the default). */
  where?: 'before' | 'after' | 'other';
}

export interface UploadStartResponse {
  uploadId: string;
  name: string;
  chunkSize: number;
}

export interface UploadProgress {
  uploadId: string;
  /** The next byte the server expects. */
  next: number;
  size: number;
  done: boolean;
  name: string;
}

/* ---------------------------------------------------------------------------------------------- */

/**
 * 1.6.7 (D-1.6.7-1): a visit's photos sit in two folders inside the visit's own folder — `Before/` (taken at the
 * Arrived step, before work) and `After/` (taken on the Finish sheet). A folder from before 1.6.7 has neither: its
 * photos are in the folder itself and nobody rearranges them.
 */
export const PHOTO_WHENS = ['before', 'after'] as const;
export type PhotoWhen = (typeof PHOTO_WHENS)[number];
export const PHOTO_SUBFOLDER: Record<PhotoWhen, string> = { before: 'Before', after: 'After' };

/** Where an office upload goes: Before, After, or the folder itself ("Other"). */
export const ADD_WHERES = ['before', 'after', 'other'] as const;
export type AddWhere = (typeof ADD_WHERES)[number];

/** `<visit folder>/Before` or `<visit folder>/After`. */
export const photoFolder = (visitFolder: string, when: PhotoWhen): string => `${visitFolder}/${PHOTO_SUBFOLDER[when]}`;

/** Which of the two a file's path is in — null for a file in the visit's folder itself (an old photo, a report). */
export function photoWhenOf(path: string, visitFolder: string): PhotoWhen | null {
  if (!path.startsWith(visitFolder + '/')) return null;
  const rest = path.slice(visitFolder.length + 1).split('/');
  if (rest.length !== 2) return null;
  return PHOTO_WHENS.find((w) => PHOTO_SUBFOLDER[w].toLowerCase() === rest[0]!.toLowerCase()) ?? null;
}
